// History-API router. Route list lives in ../../docs/routes.md — keep in sync.

import { writable, derived, type Readable } from 'svelte/store';

export interface Route {
	pattern: RegExp;
	keys: string[];
	name: string;
}

function compile(path: string): Route {
	const keys: string[] = [];
	const regex = new RegExp(
		'^' +
			path.replace(/\/:[^/]+/g, (m) => {
				keys.push(m.slice(2));
				return '/([^/]+)';
			}) +
			'$',
	);
	return { pattern: regex, keys, name: path };
}

// Route names — keep in sync with docs/routes.md and Sidebar links.
export const ROUTES = {
	login: compile('/login'),
	dashboard: compile('/'),
	events: compile('/events'),
	eventDetail: compile('/events/:id'),
	httpRequests: compile('/http-requests'),
	detections: compile('/detections'),
	detectionDetail: compile('/detections/:id'),
	banReview: compile('/ban-review'),
	bans: compile('/bans'),
	traffic: compile('/traffic'),
	// Drill-down detail pages. One per dimension that the /traffic page
	// links into. Each is a self-contained insight: scalar tiles + top
	// sub-dimensions + timeline + recent events.
	clientDetail: compile('/clients/:ip'),
	originDetail: compile('/origins/:code'),
	serviceInsights: compile('/services/:id/insights'),
	portDetail: compile('/ports/:port'),
	domains: compile('/domains'),
	domainDetail: compile('/domains/:host'),
	// Edge guard decisions, per application. See docs/edge-guard.md.
	edge: compile('/edge'),
	edgeApp: compile('/edge/:app'),
	services: compile('/services'),
	sources: compile('/sources'),
	trustedIps: compile('/trusted-ips'),
	settings: compile('/settings'),
	audit: compile('/audit'),
} as const;

export type RouteName = keyof typeof ROUTES;

export interface CurrentRoute {
	name: RouteName;
	params: Record<string, string>;
	path: string;
}

function match(path: string): CurrentRoute {
	// Route patterns are anchored on the pathname, but callers commonly pass
	// `/foo?bar=baz` (the value of `window.location.pathname + search` after a
	// navigate). Strip the query + hash first so the regex anchor still
	// matches. Without this, every link with a query string fell through to
	// the dashboard fallback — including all the /traffic drill-into links.
	const hashIdx = path.indexOf('#');
	if (hashIdx >= 0) path = path.slice(0, hashIdx);
	const qIdx = path.indexOf('?');
	const pathname = qIdx >= 0 ? path.slice(0, qIdx) : path;

	for (const [name, r] of Object.entries(ROUTES) as [RouteName, Route][]) {
		const m = pathname.match(r.pattern);
		if (!m) continue;
		const params: Record<string, string> = {};
		r.keys.forEach((k, i) => (params[k] = decodeURIComponent(m[i + 1])));
		return { name, params, path };
	}
	// Fallback to dashboard.
	return { name: 'dashboard', params: {}, path: '/' };
}

const current = writable<CurrentRoute>(match(window.location.pathname));

window.addEventListener('popstate', () => current.set(match(window.location.pathname)));
window.addEventListener('sm:unauthenticated', () => {
	navigate('/login');
});

export const route: Readable<CurrentRoute> = derived(current, ($c) => $c);

export function navigate(path: string, opts: { replace?: boolean } = {}): void {
	if (opts.replace) {
		window.history.replaceState({}, '', path);
	} else {
		window.history.pushState({}, '', path);
	}
	current.set(match(path));
}

/**
 * Client-side navigation for a click on an anchor. It is `link` as a plain
 * handler, for anchors that a component renders (`<Button href>`,
 * `<NavItem>`): a component cannot take `use:link`, but it passes `onclick`
 * through to its `<a>`. Modified clicks and absolute URLs are left to the
 * browser.
 */
export function followLink(e: MouseEvent): void {
	const node = e.currentTarget;
	if (!(node instanceof Element)) return;
	if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
	const href = node.getAttribute('href');
	if (!href || href.startsWith('http')) return;
	e.preventDefault();
	navigate(href);
}

export function link(node: HTMLAnchorElement) {
	node.addEventListener('click', followLink);
	return {
		destroy() {
			node.removeEventListener('click', followLink);
		},
	};
}
